<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Present Pregnancy Table -->
      <div class="card">
        <div class="card-header">
          <h3><i class="fas fa-baby"></i> Present Pregnancy Details</h3>
          <button class="btn-add" @click="showAddModal = true">
            <i class="fas fa-plus"></i> Add Record
          </button>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Visit Date</th>
                  <th>Gestation (Weeks)</th>
                  <th>Weight (kg)</th>
                  <th>BP</th>
                  <th>Fundal Height</th>
                  <th>Fetal Heart</th>
                  <th>Presentation</th>
                  <th>Urine Test</th>
                  <th>Complaints</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in pregnancyRecords" :key="record.id">
                  <td>{{ formatDate(record.visitDate) }}</td>
                  <td>
                    <span class="gestation-badge">{{ record.gestation }} wks</span>
                  </td>
                  <td>{{ record.weight }}</td>
                  <td>
                    <span :class="getBPClass(record.bpSystolic, record.bpDiastolic)">
                      {{ record.bpSystolic }}/{{ record.bpDiastolic }}
                    </span>
                  </td>
                  <td>{{ record.fundalHeight }} cm</td>
                  <td>
                    <span :class="getFHRClass(record.fetalHeartRate)">
                      {{ record.fetalHeartRate }} bpm
                    </span>
                  </td>
                  <td>
                    <span class="presentation-tag" :class="getPresentationClass(record.presentation)">
                      {{ record.presentation }}
                    </span>
                  </td>
                  <td>
                    <span class="urine-tag" :class="getUrineClass(record.urineTest)">
                      {{ record.urineTest }}
                    </span>
                  </td>
                  <td>
                    <span v-if="record.complaints" class="complaints-text" :title="record.complaints">
                      {{ truncateText(record.complaints, 20) }}
                    </span>
                    <span v-else class="text-muted">None</span>
                  </td>
                  <td class="actions">
                    <button class="btn-action edit" @click="editRecord(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-action delete" @click="deleteRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                    <button class="btn-action view" @click="viewDetails(record)" title="View Details">
                      <i class="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="pregnancyRecords.length === 0">
                  <td colspan="10" class="text-center text-muted">
                    No pregnancy records found. Click "Add Record" to add the first visit.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Summary Stats -->
          <div class="summary-stats">
            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-calendar-check"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ pregnancyRecords.length }}</div>
                <div class="stat-label">Total Visits</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-weight"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getLastWeight() }}</div>
                <div class="stat-label">Current Weight</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-heartbeat"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getLastBP() }}</div>
                <div class="stat-label">Current BP</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getCurrentGestation() }}</div>
                <div class="stat-label">Gestation</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Assessment -->
      <div class="card">
        <div class="card-header">
          <h3><i class="fas fa-clipboard-check"></i> Quick Assessment</h3>
        </div>
        <div class="card-body">
          <div class="assessment-grid">
            <div class="assessment-item" :class="getRiskAssessmentClass()">
              <div class="assessment-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="assessment-content">
                <h5>Risk Assessment</h5>
                <p>{{ getRiskAssessment() }}</p>
              </div>
            </div>

            <div class="assessment-item">
              <div class="assessment-icon">
                <i class="fas fa-calendar-alt"></i>
              </div>
              <div class="assessment-content">
                <h5>Next Visit Due</h5>
                <p>{{ getNextVisitDue() }}</p>
              </div>
            </div>

            <div class="assessment-item">
              <div class="assessment-icon">
                <i class="fas fa-baby"></i>
              </div>
              <div class="assessment-content">
                <h5>Fetal Well-being</h5>
                <p>{{ getFetalWellbeing() }}</p>
              </div>
            </div>

            <div class="assessment-item">
              <div class="assessment-icon">
                <i class="fas fa-notes-medical"></i>
              </div>
              <div class="assessment-content">
                <h5>Required Interventions</h5>
                <p>{{ getRequiredInterventions() }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingRecord ? 'Edit' : 'Add' }} Pregnancy Visit Record</h3>
          <button class="modal-close" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveRecord">
            <div class="form-grid">
              <div class="form-group">
                <label>Visit Date *</label>
                <input
                  type="date"
                  v-model="form.visitDate"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Gestation (Weeks) *</label>
                <input
                  type="number"
                  v-model="form.gestation"
                  min="4"
                  max="42"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Weight (kg) *</label>
                <input
                  type="number"
                  v-model="form.weight"
                  min="30"
                  max="200"
                  step="0.1"
                  required
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Blood Pressure *</label>
                <div class="bp-inputs">
                  <input
                    type="number"
                    v-model="form.bpSystolic"
                    placeholder="Systolic"
                    min="70"
                    max="200"
                    required
                    class="form-control"
                  />
                  <span class="bp-separator">/</span>
                  <input
                    type="number"
                    v-model="form.bpDiastolic"
                    placeholder="Diastolic"
                    min="40"
                    max="130"
                    required
                    class="form-control"
                  />
                </div>
              </div>

              <div class="form-group">
                <label>Fundal Height (cm)</label>
                <input
                  type="number"
                  v-model="form.fundalHeight"
                  min="0"
                  max="50"
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Fetal Heart Rate (bpm)</label>
                <input
                  type="number"
                  v-model="form.fetalHeartRate"
                  min="60"
                  max="200"
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Presentation</label>
                <select v-model="form.presentation" class="form-control">
                  <option value="">Select</option>
                  <option value="Cephalic">Cephalic</option>
                  <option value="Breech">Breech</option>
                  <option value="Transverse">Transverse</option>
                  <option value="Oblique">Oblique</option>
                  <option value="Unengaged">Unengaged</option>
                </select>
              </div>

              <div class="form-group">
                <label>Urine Test</label>
                <select v-model="form.urineTest" class="form-control">
                  <option value="">Select</option>
                  <option value="Normal">Normal</option>
                  <option value="Protein +">Protein +</option>
                  <option value="Protein ++">Protein ++</option>
                  <option value="Protein +++">Protein +++</option>
                  <option value="Glucose +">Glucose +</option>
                  <option value="Infection">Infection</option>
                </select>
              </div>

              <div class="form-group full-width">
                <label>Complaints/Symptoms</label>
                <textarea
                  v-model="form.complaints"
                  class="form-control"
                  rows="3"
                  placeholder="Any complaints, symptoms, or observations"
                ></textarea>
              </div>

              <div class="form-group full-width">
                <label>Examination Findings</label>
                <textarea
                  v-model="form.examination"
                  class="form-control"
                  rows="3"
                  placeholder="Detailed examination findings"
                ></textarea>
              </div>

              <div class="form-group full-width">
                <label>Management/Advice</label>
                <textarea
                  v-model="form.management"
                  class="form-control"
                  rows="3"
                  placeholder="Management plan and advice given"
                ></textarea>
              </div>

              <div class="form-group">
                <label>Next Visit Date</label>
                <input
                  type="date"
                  v-model="form.nextVisitDate"
                  class="form-control"
                />
              </div>

              <div class="form-group">
                <label>Seen By</label>
                <input
                  type="text"
                  v-model="form.seenBy"
                  class="form-control"
                  placeholder="Doctor/Midwife name"
                />
              </div>
            </div>

            <div class="form-preview">
              <div class="preview-item">
                <span>BP Classification:</span>
                <strong :class="getBPClass(form.bpSystolic, form.bpDiastolic)">
                  {{ classifyBP(form.bpSystolic, form.bpDiastolic) }}
                </strong>
              </div>
              <div class="preview-item">
                <span>FHR Status:</span>
                <strong :class="getFHRClass(form.fetalHeartRate)">
                  {{ classifyFHR(form.fetalHeartRate) }}
                </strong>
              </div>
              <div v-if="form.fundalHeight" class="preview-item">
                <span>Fundal Height vs Gestation:</span>
                <strong>{{ assessFundalHeight() }}</strong>
              </div>
            </div>

            <div class="modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                @click="closeModal"
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-primary">
                {{ editingRecord ? 'Update' : 'Save' }} Record
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- View Details Modal -->
    <div v-if="showDetailsModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Visit Details - {{ formatDate(viewingRecord.visitDate) }}</h3>
          <button class="modal-close" @click="showDetailsModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="details-grid">
            <div class="detail-section">
              <h5>Vital Signs</h5>
              <div class="detail-item">
                <span class="label">Gestation:</span>
                <span class="value">{{ viewingRecord.gestation }} weeks</span>
              </div>
              <div class="detail-item">
                <span class="label">Weight:</span>
                <span class="value">{{ viewingRecord.weight }} kg</span>
              </div>
              <div class="detail-item">
                <span class="label">Blood Pressure:</span>
                <span class="value" :class="getBPClass(viewingRecord.bpSystolic, viewingRecord.bpDiastolic)">
                  {{ viewingRecord.bpSystolic }}/{{ viewingRecord.bpDiastolic }} mmHg
                </span>
              </div>
            </div>

            <div class="detail-section">
              <h5>Fetal Assessment</h5>
              <div class="detail-item">
                <span class="label">Fundal Height:</span>
                <span class="value">{{ viewingRecord.fundalHeight || 'Not recorded' }} cm</span>
              </div>
              <div class="detail-item">
                <span class="label">Fetal Heart Rate:</span>
                <span class="value" :class="getFHRClass(viewingRecord.fetalHeartRate)">
                  {{ viewingRecord.fetalHeartRate || 'Not recorded' }} bpm
                </span>
              </div>
              <div class="detail-item">
                <span class="label">Presentation:</span>
                <span class="value">{{ viewingRecord.presentation || 'Not recorded' }}</span>
              </div>
            </div>

            <div class="detail-section full-width">
              <h5>Tests & Findings</h5>
              <div class="detail-item">
                <span class="label">Urine Test:</span>
                <span class="value urine-tag" :class="getUrineClass(viewingRecord.urineTest)">
                  {{ viewingRecord.urineTest || 'Not done' }}
                </span>
              </div>
              <div v-if="viewingRecord.complaints" class="detail-item">
                <span class="label">Complaints:</span>
                <span class="value">{{ viewingRecord.complaints }}</span>
              </div>
              <div v-if="viewingRecord.examination" class="detail-item">
                <span class="label">Examination Findings:</span>
                <span class="value">{{ viewingRecord.examination }}</span>
              </div>
            </div>

            <div class="detail-section full-width">
              <h5>Management</h5>
              <div class="detail-item">
                <span class="label">Management/Advice:</span>
                <span class="value">{{ viewingRecord.management || 'Not specified' }}</span>
              </div>
              <div v-if="viewingRecord.nextVisitDate" class="detail-item">
                <span class="label">Next Visit:</span>
                <span class="value">{{ formatDate(viewingRecord.nextVisitDate) }}</span>
              </div>
              <div v-if="viewingRecord.seenBy" class="detail-item">
                <span class="label">Seen By:</span>
                <span class="value">{{ viewingRecord.seenBy }}</span>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showDetailsModal = false"
            >
              Close
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="editRecord(viewingRecord)"
            >
              Edit Record
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";

export default {
  name: "PresentPregnancy",
  components: {
    full_head,
  },
  data() {
    return {
      showAddModal: false,
      showDetailsModal: false,
      editingRecord: null,
      viewingRecord: null,
      form: {
        visitDate: new Date().toISOString().split("T")[0],
        gestation: 12,
        weight: 60,
        bpSystolic: 120,
        bpDiastolic: 80,
        fundalHeight: "",
        fetalHeartRate: "",
        presentation: "",
        urineTest: "",
        complaints: "",
        examination: "",
        management: "",
        nextVisitDate: "",
        seenBy: "",
      },
      pregnancyRecords: [
        {
          id: 1,
          visitDate: "2023-09-01",
          gestation: 12,
          weight: 58.5,
          bpSystolic: 110,
          bpDiastolic: 70,
          fundalHeight: "",
          fetalHeartRate: "",
          presentation: "",
          urineTest: "Normal",
          complaints: "Mild nausea",
          examination: "Uterus just palpable above symphysis",
          management: "Start iron and folic acid supplementation",
          nextVisitDate: "2023-10-01",
          seenBy: "Dr. Jane Smith",
        },
        {
          id: 2,
          visitDate: "2023-10-05",
          gestation: 16,
          weight: 59.2,
          bpSystolic: 115,
          bpDiastolic: 75,
          fundalHeight: 16,
          fetalHeartRate: 155,
          presentation: "",
          urineTest: "Normal",
          complaints: "None",
          examination: "Fundal height corresponds to dates",
          management: "Continue supplements, advice on nutrition",
          nextVisitDate: "2023-11-02",
          seenBy: "Nurse Mary Johnson",
        },
        {
          id: 3,
          visitDate: "2023-11-02",
          gestation: 20,
          weight: 61.5,
          bpSystolic: 118,
          bpDiastolic: 78,
          fundalHeight: 20,
          fetalHeartRate: 148,
          presentation: "Cephalic",
          urineTest: "Normal",
          complaints: "Backache",
          examination: "Fetal movements felt, fundal height appropriate",
          management: "Anomaly scan advised, back care advice",
          nextVisitDate: "2023-12-07",
          seenBy: "Dr. Jane Smith",
        },
      ],
    };
  },
  computed: {
    sortedRecords() {
      return [...this.pregnancyRecords].sort(
        (a, b) => new Date(b.visitDate) - new Date(a.visitDate)
      );
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },

    getBPClass(systolic, diastolic) {
      if (!systolic || !diastolic) return "";
      if (systolic >= 140 || diastolic >= 90) return "bp-high";
      if (systolic >= 130 || diastolic >= 85) return "bp-elevated";
      if (systolic < 90 || diastolic < 60) return "bp-low";
      return "bp-normal";
    },

    getFHRClass(fhr) {
      if (!fhr) return "";
      if (fhr < 110) return "fhr-low";
      if (fhr > 160) return "fhr-high";
      return "fhr-normal";
    },

    getPresentationClass(presentation) {
      const classes = {
        Cephalic: "presentation-cephalic",
        Breech: "presentation-breech",
        Transverse: "presentation-transverse",
        Oblique: "presentation-oblique",
        Unengaged: "presentation-unengaged",
      };
      return classes[presentation] || "";
    },

    getUrineClass(urineTest) {
      if (!urineTest || urineTest === "Normal") return "urine-normal";
      if (urineTest.includes("Protein")) return "urine-protein";
      if (urineTest.includes("Glucose")) return "urine-glucose";
      if (urineTest === "Infection") return "urine-infection";
      return "";
    },

    classifyBP(systolic, diastolic) {
      if (!systolic || !diastolic) return "Not recorded";
      if (systolic >= 140 || diastolic >= 90) return "Hypertensive";
      if (systolic >= 130 || diastolic >= 85) return "Elevated";
      if (systolic < 90 || diastolic < 60) return "Low";
      return "Normal";
    },

    classifyFHR(fhr) {
      if (!fhr) return "Not recorded";
      if (fhr < 110) return "Bradycardia";
      if (fhr > 160) return "Tachycardia";
      return "Normal";
    },

    assessFundalHeight() {
      if (!this.form.fundalHeight || !this.form.gestation) return "";
      const expected = this.form.gestation; // Fundal height should roughly equal gestation
      const actual = this.form.fundalHeight;
      const difference = Math.abs(actual - expected);
      
      if (difference <= 2) return "Appropriate for dates";
      if (actual > expected) return "Large for dates";
      return "Small for dates";
    },

    getLastWeight() {
      if (this.pregnancyRecords.length === 0) return "-";
      const lastRecord = this.sortedRecords[0];
      return `${lastRecord.weight} kg`;
    },

    getLastBP() {
      if (this.pregnancyRecords.length === 0) return "-";
      const lastRecord = this.sortedRecords[0];
      return `${lastRecord.bpSystolic}/${lastRecord.bpDiastolic}`;
    },

    getCurrentGestation() {
      if (this.pregnancyRecords.length === 0) return "-";
      const lastRecord = this.sortedRecords[0];
      return `${lastRecord.gestation} wks`;
    },

    getRiskAssessment() {
      if (this.pregnancyRecords.length === 0) return "No records available";
      
      const lastRecord = this.sortedRecords[0];
      const riskFactors = [];
      
      // Check for risk factors
      if (lastRecord.bpSystolic >= 140 || lastRecord.bpDiastolic >= 90) {
        riskFactors.push("Hypertension");
      }
      if (lastRecord.urineTest && lastRecord.urineTest.includes("Protein")) {
        riskFactors.push("Proteinuria");
      }
      if (lastRecord.fetalHeartRate && (lastRecord.fetalHeartRate < 110 || lastRecord.fetalHeartRate > 160)) {
        riskFactors.push("Abnormal FHR");
      }
      
      if (riskFactors.length === 0) return "Low Risk";
      if (riskFactors.length === 1) return `Medium Risk (${riskFactors[0]})`;
      return `High Risk (${riskFactors.join(", ")})`;
    },

    getRiskAssessmentClass() {
      const assessment = this.getRiskAssessment();
      if (assessment.includes("High Risk")) return "risk-high";
      if (assessment.includes("Medium Risk")) return "risk-medium";
      return "risk-low";
    },

    getNextVisitDue() {
      if (this.pregnancyRecords.length === 0) return "Schedule first visit";
      
      const lastRecord = this.sortedRecords[0];
      if (lastRecord.nextVisitDate) {
        const today = new Date();
        const nextVisit = new Date(lastRecord.nextVisitDate);
        const daysDiff = Math.ceil((nextVisit - today) / (1000 * 60 * 60 * 24));
        
        if (daysDiff < 0) return `Overdue by ${Math.abs(daysDiff)} days`;
        if (daysDiff <= 7) return `Due in ${daysDiff} days`;
        return this.formatDate(lastRecord.nextVisitDate);
      }
      return "Not scheduled";
    },

    getFetalWellbeing() {
      if (this.pregnancyRecords.length === 0) return "No fetal assessment";
      
      const lastRecord = this.sortedRecords[0];
      if (!lastRecord.fetalHeartRate) return "Not assessed";
      
      if (lastRecord.fetalHeartRate >= 110 && lastRecord.fetalHeartRate <= 160) {
        return "Normal fetal heart rate";
      }
      return "Needs monitoring";
    },

    getRequiredInterventions() {
      if (this.pregnancyRecords.length === 0) return "First ANC visit needed";
      
      const lastRecord = this.sortedRecords[0];
      const interventions = [];
      
      if (lastRecord.gestation >= 28 && !lastRecord.urineTest) {
        interventions.push("Urine test");
      }
      if (lastRecord.bpSystolic >= 140 || lastRecord.bpDiastolic >= 90) {
        interventions.push("BP monitoring");
      }
      if (lastRecord.fetalHeartRate < 110 || lastRecord.fetalHeartRate > 160) {
        interventions.push("Fetal monitoring");
      }
      
      if (interventions.length === 0) return "No interventions required";
      return interventions.join(", ");
    },

    editRecord(record) {
      this.editingRecord = record;
      this.form = { ...record };
      this.showAddModal = true;
      this.showDetailsModal = false;
    },

    viewDetails(record) {
      this.viewingRecord = record;
      this.showDetailsModal = true;
    },

    deleteRecord(id) {
      if (confirm("Are you sure you want to delete this pregnancy record?")) {
        this.pregnancyRecords = this.pregnancyRecords.filter(
          (record) => record.id !== id
        );
      }
    },

    closeModal() {
      this.showAddModal = false;
      this.editingRecord = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        visitDate: new Date().toISOString().split("T")[0],
        gestation: this.getNextGestation(),
        weight: this.getLastWeightValue(),
        bpSystolic: 120,
        bpDiastolic: 80,
        fundalHeight: "",
        fetalHeartRate: "",
        presentation: "",
        urineTest: "",
        complaints: "",
        examination: "",
        management: "",
        nextVisitDate: "",
        seenBy: "",
      };
    },

    getNextGestation() {
      if (this.pregnancyRecords.length === 0) return 12;
      const lastRecord = this.sortedRecords[0];
      const weeksSinceLast = Math.floor(
        (new Date() - new Date(lastRecord.visitDate)) / (1000 * 60 * 60 * 24 * 7)
      );
      return Math.min(lastRecord.gestation + weeksSinceLast, 42);
    },

    getLastWeightValue() {
      if (this.pregnancyRecords.length === 0) return 60;
      return this.sortedRecords[0].weight;
    },

    saveRecord() {
      const recordData = {
        ...this.form,
        id: this.editingRecord ? this.editingRecord.id : Date.now(),
      };

      if (this.editingRecord) {
        const index = this.pregnancyRecords.findIndex(
          (record) => record.id === this.editingRecord.id
        );
        this.pregnancyRecords[index] = recordData;
      } else {
        this.pregnancyRecords.push(recordData);
      }

      // Sort by date (newest first)
      this.pregnancyRecords.sort(
        (a, b) => new Date(b.visitDate) - new Date(a.visitDate)
      );

      this.closeModal();
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.main-content {
  margin-top: 20px;
}

.card {
  background: white;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 20px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.btn-add {
  padding: 8px 20px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
}

.btn-add:hover {
  background: #218838;
}

.card-body {
  padding: 25px;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 30px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.gestation-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #e8f4fd;
  color: #2980b9;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.bp-normal {
  color: #27ae60;
  font-weight: 600;
}

.bp-elevated {
  color: #f39c12;
  font-weight: 600;
}

.bp-high {
  color: #e74c3c;
  font-weight: 600;
}

.bp-low {
  color: #3498db;
  font-weight: 600;
}

.fhr-normal {
  color: #27ae60;
  font-weight: 600;
}

.fhr-low, .fhr-high {
  color: #e74c3c;
  font-weight: 600;
}

.presentation-tag {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.presentation-cephalic {
  background: #d4edda;
  color: #155724;
}

.presentation-breech {
  background: #fff3cd;
  color: #856404;
}

.presentation-transverse,
.presentation-oblique,
.presentation-unengaged {
  background: #f8d7da;
  color: #721c24;
}

.urine-tag {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.urine-normal {
  background: #d4edda;
  color: #155724;
}

.urine-protein {
  background: #f8d7da;
  color: #721c24;
}

.urine-glucose {
  background: #fff3cd;
  color: #856404;
}

.urine-infection {
  background: #d1ecf1;
  color: #0c5460;
}

.complaints-text {
  font-size: 12px;
  color: #6c757d;
  cursor: help;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.actions {
  display: flex;
  gap: 5px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action.edit {
  background: #3498db;
  color: white;
}

.btn-action.edit:hover {
  background: #2980b9;
}

.btn-action.delete {
  background: #e74c3c;
  color: white;
}

.btn-action.delete:hover {
  background: #c0392b;
}

.btn-action.view {
  background: #6c757d;
  color: white;
}

.btn-action.view:hover {
  background: #5a6268;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
}

.stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
  font-weight: 600;
}

.assessment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.assessment-item {
  display: flex;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  align-items: center;
  border-left: 5px solid #dee2e6;
}

.assessment-item.risk-low {
  border-left-color: #28a745;
}

.assessment-item.risk-medium {
  border-left-color: #ffc107;
}

.assessment-item.risk-high {
  border-left-color: #dc3545;
}

.assessment-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;
  background: white;
}

.risk-low .assessment-icon {
  color: #28a745;
  border: 2px solid #28a745;
}

.risk-medium .assessment-icon {
  color: #ffc107;
  border: 2px solid #ffc107;
}

.risk-high .assessment-icon {
  color: #dc3545;
  border: 2px solid #dc3545;
}

.assessment-content {
  flex: 1;
}

.assessment-content h5 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
}

.assessment-content p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 30px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #495057;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.bp-inputs {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bp-inputs .form-control {
  flex: 1;
}

.bp-separator {
  font-weight: bold;
  color: #495057;
}

.form-preview {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.preview-item {
  text-align: center;
}

.preview-item span {
  display: block;
  color: #7f8c8d;
  font-size: 14px;
  margin-bottom: 5px;
}

.preview-item strong {
  font-size: 16px;
  color: #2c3e50;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.detail-section {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.detail-section.full-width {
  grid-column: 1 / -1;
}

.detail-section h5 {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 2px solid #dee2e6;
}

.detail-item {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
}

.detail-item .label {
  font-weight: 600;
  color: #6c757d;
}

.detail-item .value {
  color: #2c3e50;
  text-align: right;
  max-width: 70%;
}

@media (max-width: 1200px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .summary-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .assessment-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .container-fluid {
    padding: 10px;
  }
  
  .table {
    font-size: 14px;
  }
  
  .table th,
  .table td {
    padding: 8px;
  }
  
  .summary-stats {
    grid-template-columns: 1fr;
  }
  
  .assessment-grid {
    grid-template-columns: 1fr;
  }
  
  .card-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .btn-add {
    align-self: stretch;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .table-responsive {
    overflow-x: auto;
  }
  
  .table {
    min-width: 800px;
  }
  
  .bp-inputs {
    flex-direction: column;
    gap: 5px;
  }
  
  .bp-separator {
    display: none;
  }
}
</style>